import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import type { SeoMeta } from "@/types"

interface LocationMeta {
  slug: string
  name: string
  description: string
}

const LOCATION_META: Record<string, LocationMeta> = {
  bristol: {
    slug: "bristol",
    name: "Bristol",
    description:
      "A thriving south-west city with strong demand, vibrant neighbourhoods, and a diverse property market.",
  },
  brighton: {
    slug: "brighton",
    name: "Brighton",
    description:
      "Coastal living with London commuter appeal. High demand, compact supply, and premium prices by the sea.",
  },
  bath: {
    slug: "bath",
    name: "Bath",
    description:
      "Georgian architecture, heritage charm, and a premium market driven by international buyers and families.",
  },
  oxford: {
    slug: "oxford",
    name: "Oxford",
    description:
      "A university city with constrained supply, strong rental demand, and consistently rising property values.",
  },
}

interface PageProps {
  meta: SeoMeta
  locations: string[]
}

export default function InsightsIndexPage({ meta, locations }: PageProps) {
  const locationCards = locations
    .map((slug) => LOCATION_META[slug])
    .filter(Boolean)

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="bg-cream px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Live data
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
            Property market insights
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-slate">
            Sold prices, price trends, crime statistics, and local amenities for
            UK cities. All powered by open government data, updated regularly.
          </p>
        </div>
      </section>

      {/* Location cards */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal stagger className="grid gap-6 sm:grid-cols-2">
            {locationCards.map((loc) => (
              <Link
                key={loc.slug}
                href={`/insights/${loc.slug}`}
                className="group flex flex-col rounded-xl border border-stone bg-white p-6 transition-all hover:border-red/20 hover:shadow-md hover:shadow-red/5"
              >
                <h2 className="font-display text-xl font-bold text-charcoal transition-colors group-hover:text-red">
                  {loc.name}
                </h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate">
                  {loc.description}
                </p>
                <p className="mt-4 flex items-center gap-1.5 text-sm font-medium text-red">
                  View market data
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-4 transition-transform group-hover:translate-x-1"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* More resources */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="text-center font-display text-2xl font-bold text-charcoal">
              More property resources
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal stagger className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              {
                href: "/tools/postcode-report",
                title: "Postcode report",
                desc: "Free neighbourhood report for any UK postcode.",
              },
              {
                href: "/tools",
                title: "Calculators",
                desc: "Stamp duty, mortgage, commission, and more.",
              },
              {
                href: "/areas",
                title: "Area guides",
                desc: "In-depth guides to property hotspots.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Turn data into listings sold faster
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight turns your listings into scroll-stopping video ads.
            Pair market insights with professional property videos.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/features"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              See features
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
